import type { ExportBilateralTradesRow } from '@ebalance/proto/bffuser/v1/supply_bilateral_trade_model_pb'

import type { BilateralTradeCsvColumn } from '~/models/bilateralTradeCsv'
import {
  bilateralTradeCsvDataTypeName,
  serializeBilateralTradeCsvRows,
} from '~/models/bilateralTradeCsv'
import { commonUploadCsvFileName } from '~/pages/AdminUploads/models/downloadFileName'

/**
 * 相対取引契約 CSV エクスポートの行変換・CSV 組み立て
 * (uploads/common.md §6 AC-DUC-028/029、列定義は
 * docs/specs/admin/data/uploads/bilateral-trade.md 第2節の 20 列)。
 *
 * **ここに変換ロジックは無い**。相対取引の CSV 語彙 (「定型」「買」「東京」
 * 「BG」…) は表示文言ではなく取込パーサが受理する交換フォーマットの値そのもの
 * で、その受理表の SoT は backend
 * (`go/internal/backend/application/usecase/supply/bilateral_trade_csv_vocab.go`)
 * にある。加えて 自社エリア の逆写像 (sell/buy のどちらを読むかが 売買 で
 * 決まる)・商品形態・日区分・時間帯区分 の復元は業務ルールで、frontend に
 * 二重実装しない。よって backend は CSV セル値の
 * 文字列をそのまま返し、ここは **proto フィールド → 列名** の写経と列順への
 * 並べ替えだけを行う (他の種別は enum を受けて frontend でラベル化しており、
 * 本種別だけ非対称なのはこの理由による)。英語表示では request の `language` に
 * ENGLISH を載せ、区分値のセルは backend が英語で返す (frontend は語彙を持たない)。
 * ここが表示言語で変えるのはヘッダー行とファイル名だけ。
 */

/** CSV 1 行分のセル値 (列名キー、値はそのまま書き出せる文字列)。 */
export type BilateralTradeCsvExportRow = Record<BilateralTradeCsvColumn, string>

export function toBilateralTradeCsvExportRow(
  row: ExportBilateralTradesRow,
): BilateralTradeCsvExportRow {
  return {
    約定経路: row.executionRoute,
    約定日: row.executionDate,
    取引ID: row.publicTradeId,
    自社側事業者コード: row.ownBusinessCode,
    '自社側BG/計画提出者コード': row.ownSubmissionEntityCode,
    取引先: row.partnerName,
    '取引先BG/計画提出者コード': row.partnerSubmissionEntityCode,
    商品形態: row.productType,
    受渡区分: row.deliveryType,
    売買: row.buySell,
    自社エリア: row.areaCode,
    精算単価参照エリア: row.settlementPriceAreaCode,
    開始日: row.periodFrom,
    終了日: row.periodTo,
    日区分: row.dayCategory,
    時間帯区分: row.timeSlots,
    受給日カレンダー: row.calendarKey,
    '数量 (MW)': row.volumeMw,
    '単価 (円/kWh)': row.unitPrice,
    delete: row.delete,
  }
}

/** 応答全行を CSV 行へ変換する (順序は backend が決めた並びを保つ)。 */
export function toBilateralTradeCsvExportRows(
  rows: readonly ExportBilateralTradesRow[],
): BilateralTradeCsvExportRow[] {
  return rows.map(toBilateralTradeCsvExportRow)
}

/**
 * エクスポート行から CSV 本文を組み立てる (F1 純関数)。
 *
 * テンプレート DL と同じ `serializeBilateralTradeCsvRows` を使う: 列ヘッダーと
 * 列順の SoT (`BILATERAL_TRADE_CSV_COLUMNS`) を共有し、`parseBilateralTradeCsv`
 * との往復可逆性テストがそのまま出力側にも効く (= 列ズレを構造的に防ぐ)。
 * BOM は付けない (`~/libs/downloadCsv` が保存時に付与する)。
 */
export function buildBilateralTradeCsvExport(
  rows: readonly BilateralTradeCsvExportRow[],
  isEnglish: boolean,
): string {
  return serializeBilateralTradeCsvRows([...rows], isEnglish)
}

/**
 * 入稿済みデータのファイル名 (`{データ種別名}_{YYYYMMDD}.csv`、common.md §6。
 * 英語表示は英語データ種別名 `bilateral-trade`)。
 */
export function bilateralTradeCsvExportFileName(
  isEnglish: boolean,
  today?: Date,
): string {
  return commonUploadCsvFileName(
    bilateralTradeCsvDataTypeName(isEnglish),
    today,
  )
}
